// BE#280 / #892 — "No LID for user" is WhatsApp's own generic error and conflates two
// causes: a canonicalization / local-mapping miss (FE#224) AND a number that is
// genuinely not on WhatsApp. On the send path an unreachable number surfaced this
// text raw as `PAGE_HANDLER_THREW` / `SEND_THREW`, which reads as an internal bug
// and triggered false "FE#224 is broken" escalations (e.g. a cold Rondônia/DDD-69
// blast that is ~94% not-on-WhatsApp).

/**
 * VALIDATION-path predicate. True when a WhatsApp error message means the
 * recipient is not reachable on WhatsApp (either genuinely not registered, or
 * WhatsApp could not resolve it). Used by validation-operations.ts, whose
 * `technicalError` comes from the narrow contact-resolution flow where a bare
 * `not found` unambiguously means "no such WhatsApp user".
 */
export function isNotOnWhatsAppError(message: string | null | undefined): boolean {
  if (!message) return false;
  return message.includes('No LID for user') || message.includes('not found');
}

/**
 * Matches WA Web's "No LID for user" (and nothing broader — a bare `not found`
 * on the send path is a store desync).
 *
 * WA Web source (dump 2026-10-22): `WAWebLidMigrationUtils.toUserLidOrThrow`
 * calls `WAWebApiContact.getCurrentLid(wid)` — a read of the LOCAL lid↔pn
 * mapping — and throws `err("No LID for user")` when it is empty. That is a
 * local-store miss, NOT a server verdict: the server's answer to "does this
 * number have an account" is `WAWebQueryExistsJob` (a usync contact query),
 * which is what the pre-send recipient gate runs. A real number can therefore
 * hit this error transiently (mapping not yet synced), and it succeeds on a
 * later attempt — #892: agent operator notices to a working chat failed 61×/7d
 * and were recorded as `not_on_whatsapp`.
 *
 * So this predicate is for diagnostics (send-trace stamping) and the LID
 * retry in resolve-contact-chat, NEVER for assigning the `not_on_whatsapp`
 * failure code on the send path. That code comes only from the engine's
 * definite answer (`validateChatId` / an EngineToolError `not_on_whatsapp`).
 */
export function isNoLidForUserError(message: string | null | undefined): boolean {
  if (!message) return false;
  return message.includes('No LID for user');
}
